import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, ArrowRight, MapPin } from 'lucide-react';
import { collection, getDocs, orderBy, query, limit } from 'firebase/firestore';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { db } from '../utils/firebase';
import { formatDateForDisplay } from '../utils/dateUtils';
import LazyFirebaseImage from './LazyFirebaseImage';
import Reveal from './Reveal';
import { setResolvedImageUrl } from '../utils/imageCache';

interface Activity {
  id: string;
  title: string;
  date: string;
  content: string;
  imageUrl: string;
    location?: string;
}

const Activities: React.FC = () => {
  const navigate = useNavigate();
  const [activities, setActivities] = useState<Activity[]>([]);

  useEffect(() => {
    const fetchActivities = async () => {
      const storage = getStorage();
      const resolveImageUrl = async (path: string): Promise<string> => {
        try {
          if (!path) return '';
          if (path.startsWith('http')) return path;
          const imageRef = ref(storage, path);
          const url = await getDownloadURL(imageRef);
          return url;
        } catch (_) {
          return '';
        }
      };

      const activitiesCollection = collection(db, 'activities');
      const q = query(activitiesCollection, orderBy('date', 'desc'), limit(12));
      const querySnapshot = await getDocs(q);
      const activitiesList = await Promise.all(querySnapshot.docs.map(async (doc) => {
        const data = doc.data();
        const rawImage = typeof data.imageUrl === 'string' ? data.imageUrl : '';
        const resolvedImageUrl = await resolveImageUrl(rawImage);
        if (resolvedImageUrl) setResolvedImageUrl(rawImage, resolvedImageUrl);
        return {
          id: doc.id,
          title: data.title,
          date: data.date,
          content: data.content,
          imageUrl: resolvedImageUrl,
          location: data.location || '',
        } as Activity;
      }));
      setActivities(activitiesList);

      // Prefetch top images to warm the browser cache
      activitiesList.slice(0, 12).forEach((activity) => {
        if (activity.imageUrl) {
          const img = new Image();
          // Hint decoding without blocking layout
          (img as any).decoding = 'async';
          img.src = activity.imageUrl;
        }
      });
    };

    fetchActivities();
  }, []);

  const handleCardClick = (activityId: string) => {
    navigate(`/activities-detail/${activityId}`);
  };

  return (
    <section id="activities" className="section bg-white">
      <div className="container">
        <Reveal>
        <Reveal className="text-center mb-12" delay={0.05}>
          <h2 className="section-title">
            活動報告
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto text-center">みなさまとの活動の機会を大切にしてきました。<br />これからも大切にします。</p>
        </Reveal>

        <div className="max-w-7xl mx-auto">
          {/* カードグリッド（塊で同時に表示） */}
          <Reveal delay={0.15}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
              {activities.map((activity, index) => (
                <div
                  key={activity.id}
                  className={`bg-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer transform hover:-translate-y-1 h-full ${index >= 6 ? 'hidden lg:flex lg:flex-col' : 'flex flex-col'}`}
                  onClick={() => handleCardClick(activity.id)}
                >
                  {/* 画像 */}
                  <div className="relative aspect-[16/9] rounded-t-lg bg-gray-50 overflow-hidden">
                    <LazyFirebaseImage
                      pathOrUrl={activity.imageUrl}
                      alt={activity.title}
                      loading={index < 4 ? 'eager' : 'lazy'}
                      decoding="async"
                      fetchPriority={index < 4 ? 'high' : 'low'}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* カード内容 */}
                  <div className="p-4 flex flex-col flex-1">
                    <div className="min-h-[3.5rem] mb-2 flex items-center">
                      <h3 className="text-lg font-bold text-gray-800 line-clamp-2">
                        {activity.title}
                      </h3>
                    </div>
                    
                    <div className="space-y-2 mb-3">
                      <div className="flex items-center text-sm text-gray-600">
                        <Calendar className="w-4 h-4 mr-2 flex-shrink-0" />
                        <span>{formatDateForDisplay(new Date(activity.date))}</span>
                      </div>
                      {activity.location && (
                        <div className="flex items-center text-sm text-gray-600">
                          <MapPin className="w-4 h-4 mr-2 flex-shrink-0" />
                          <span className="truncate">{activity.location}</span>
                        </div>
                      )}
                    </div>

                    <p className="text-gray-600 text-sm line-clamp-3 mb-4 flex-grow">
                      {activity.content.substring(0, 50)}...
                    </p>

                    <div className="flex items-center justify-between mt-auto">
                      <span className="text-pink-500 text-sm font-medium">
                        詳細を見る
                      </span>
                      <ArrowRight className="w-4 h-4 text-pink-500" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>

          {/* 全ての活動を見るボタン */}
          <Reveal className="text-center" delay={0.3}>
            <button
              className="btn text-base px-8 py-3"
              onClick={() => navigate('/activities-detail', { state: { from: 'home' as const } })}
            >
              全ての活動報告を見る
            </button>
          </Reveal>
        </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Activities; 